// ColorButton.jsx

import React, { useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './ColorButton.css';

// Components
import Popup from 'components/popup/Popup';

const TRANSPARENT_OPTION_KEY = '__transparent__';

/**
 * ColorButton
 *
 * Renders a color picker with theme palette options and a clear/transparent state.
 * Use variant="inline" to embed the swatches in place (no popup).
 *
 * @param {string} color - The currently selected hex color, or an empty string for transparent.
 * @param {function} onChange - Called with the next selected color value.
 * @param {boolean} disabled - Whether the color picker should be disabled.
 * @param {number} popupZIndex - The z-index applied to the palette popup.
 * @param {'popup'|'inline'} variant - Presentation mode.
 *
 * @returns {ReactNode} The rendered ColorButton.
 */
const ColorButton = React.memo(({
    color = '',
    onChange,
    disabled = false,
    popupZIndex = 2000,
    variant = 'popup',
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    const colorOptions = theme.colorOptions || [];
    const isTransparentSelected = !color;
    const isInline = variant === 'inline';

    // ----- STATE ----- //

    const [open, setOpen] = useState(false);

    // ----- REFS ----- //

    const anchorRef = useRef(null);

    // ----- HANDLERS ----- //

    const handleSelect = (nextColor) => {
        if (disabled) return;
        onChange?.(nextColor);
        if (!isInline) setOpen(false);
    };

    const renderPalette = (className) => (
        <div className={className}>
            <button
              className={`color-button-option color-button-option-transparent ${isTransparentSelected ? 'selected' : ''}`}
              type="button"
              aria-label="Select transparent"
              disabled={disabled}
              onClick={() => handleSelect('')}
            />

            {colorOptions.map((swatch) => {
                const isSelected = swatch === color;
                return (
                    <button
                      className={`color-button-option ${isSelected ? 'selected' : ''}`}
                      style={{
                                backgroundColor: swatch,
                            }}
                      key={`${TRANSPARENT_OPTION_KEY}-${swatch}`}
                      type="button"
                      aria-label={`Select ${swatch}`}
                      disabled={disabled}
                      onClick={() => handleSelect(swatch)}
                    />
                );
            })}
        </div>
    );

    // ----- RENDER ----- //

    if (isInline) {
        return (
            <div
              className={`color-button-inline${disabled ? ' disabled' : ''}`}
              role="group"
              aria-label="Select color"
            >
                {renderPalette('color-button-inline-palette')}
            </div>
        );
    }

    return (
        <>
            <div
              className={`color-button-container ${disabled ? 'disabled' : ''}`}
              ref={anchorRef}
              role="button"
              tabIndex={disabled ? -1 : 0}
              aria-label="Select color"
              onClick={() => {
                    if (!disabled) setOpen(true);
                }}
              onKeyDown={(event) => {
                    if (disabled) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setOpen(true);
                    }
                }}
            >

                {/* SWATCH */}
                <div
                  className={`color-button-swatch ${color ? '' : 'empty'}`}
                  style={{
                    backgroundColor: color || 'transparent',
                  }}
                />
            </div>

            <Popup anchorRef={anchorRef} isOpen={open} setOpen={setOpen} zIndex={popupZIndex}>
                {renderPalette('color-button-popup')}
            </Popup>
        </>
    );

});

// Dev Tools
ColorButton.displayName = 'ColorButton';

export default ColorButton;
